import {
  group,
  groupsCss,
  kitPartCss,
  microHeading,
  microHeadingCss,
  panel,
  panelAction,
  panelActionCss,
  panelCss,
  rowList,
  rowsCss,
} from '../armKit';
import { cvUrl, mentionsUrl, networkUrl, searchPath } from '../destinations';
import { fieldFeedWorks } from '../pageData';
import {
  interests,
  profileGaps,
  viewedPageName,
  viewedUser,
} from '../profileFacts';
import { INK, mentionsStatUrl, WARM_HAIRLINE } from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { FeedPaper } from '../pageData';
import type { ViewedUser } from '../profileFacts';

const CV_SOURCE = 'profile-liautotest-end-cv';
const NETWORK_SOURCE = 'profile-liautotest-end-network';
const MENTIONS_SOURCE = 'profile-liautotest-end-mentions';
const FIELD_SOURCE = 'profile-liautotest-end-field';
const FIELD_PAPER_SOURCE = 'profile-liautotest-end-field-paper';

const FIELD_LIMIT = 2;
const BAND_SIZE = 3;
const MAX_NAMES = 3;

const AUTHORS_CLASS = 'profile-liautotest-end__authors';

type Footer = { pageName: string; name: string; firstName: string };

type Field = { id: number; name: string };

// display_name is the serializer's preformatted full name; the two required
// name keys are the fallback for a payload that omits it.
function nameOf(user: ViewedUser): string {
  const display = user.display_name?.trim() ?? '';

  return display !== ''
    ? display
    : `${user.first_name} ${user.last_name}`.trim();
}

// The one predicate the stamp and the gate share: a zero-works profile has no
// list to end, and without a page name the footer has no destinations.
function footerFacts(ctx: ArmContext): Footer | null {
  if (ctx.cards().length === 0) return null;

  const user = viewedUser();
  const pageName = viewedPageName();
  if (user == null || pageName == null || pageName === '') return null;

  const name = nameOf(user);
  if (name === '') return null;

  return { pageName, name, firstName: user.first_name.trim() };
}

// Deliberately number-free: the section chips count Videos and Courses too,
// so any "all N papers" figure can contradict the very list it caps. The name
// is the only fact the line leans on.
function endLine(firstName: string): string | null {
  if (firstName === '') return null;

  return `You've reached the end of ${firstName}'s uploads`;
}

function linkRow(
  title: string,
  url: string,
  source: string,
  ctx: ArmContext,
): Row {
  return { title, url, onClick: () => ctx.track(source) };
}

// No Citations row on purpose: the per-user citations page is gated on the
// VIEWER's citations-dashboard eligibility, so for most logged-in visitors it
// is a 404 rather than a destination.
function linkRows(pageName: string, ctx: ArmContext): Row[] {
  // The CV route 404s on a profile with no CV attachment. The page renders
  // its own CV link exactly when one exists, and profileGaps' cv probe reads
  // that link — true means MISSING.
  const cv = profileGaps().cv
    ? []
    : [linkRow('CV', cvUrl(pageName), CV_SOURCE, ctx)];

  return [...cv, linkRow('Network', networkUrl(pageName), NETWORK_SOURCE, ctx)];
}

// rowList is the only way this file builds an <li>, so the late row goes
// through it too — a one-element list whose child moves across.
function appendRow(list: HTMLElement, row: Row): void {
  const item = rowList([row]).firstElementChild;
  if (item != null) list.appendChild(item);
}

// The mentions page 404s for a user with no qualifying mentions, and the
// page's own verdict is its async-filled Mentions stat anchor — a fetch after
// DOMContentLoaded fills it, so null at render is "not yet" as much as "no".
// The two timers re-ask for the settled answer and append the row the moment
// the anchor lands; the href stays mentionsUrl's, which is the same URL the
// anchor carries. This page has no teardown, so a timer firing on a settled
// null does nothing and needs no cleanup.
function appendMentionsWhenVerified(
  list: HTMLElement,
  pageName: string,
  ctx: ArmContext,
): void {
  let appended = false;
  const probe = (): void => {
    if (appended || mentionsStatUrl() == null) return;
    appended = true;
    appendRow(
      list,
      linkRow('Mentions', mentionsUrl(pageName), MENTIONS_SOURCE, ctx),
    );
  };

  probe();
  if (appended) return;

  [1500, 4000].forEach((delay) => window.setTimeout(probe, delay));
}

// No entityId on the field source: an interest is not a work, and a long name
// can push the extra over budget, where the tracking ladder sheds it rather
// than losing the click.
function trackField(field: Field, ctx: ArmContext): void {
  ctx.track(FIELD_SOURCE, { extra: { interest: field.name } });
}

function searchRow(field: Field, ctx: ArmContext): Row {
  return {
    title: `Recent work on ${field.name}`,
    url: searchPath(field.name),
    onClick: () => trackField(field, ctx),
  };
}

function authorLine(names: string[]): string {
  if (names.length <= MAX_NAMES) return names.join(', ');

  return `${names.slice(0, MAX_NAMES).join(', ')} et al.`;
}

// The authors lead every paper row: the band's claim is OTHER researchers,
// and the names are the proof. A paper the feed names no authors for gets no
// line at all rather than a guessed one.
function paperList(papers: FeedPaper[], ctx: ArmContext): HTMLElement {
  const list = rowList(
    papers.map((paper) => ({
      title: paper.title,
      url: paper.url,
      onClick: () => ctx.track(FIELD_PAPER_SOURCE, { entityId: paper.id }),
    })),
  );

  Array.from(list.children).forEach((item, index) => {
    const names = papers[index].authorNames;
    if (names.length === 0) return;

    const authors = document.createElement('span');
    authors.className = AUTHORS_CLASS;
    authors.textContent = authorLine(names);
    item.insertBefore(authors, item.firstChild);
  });

  return list;
}

// A field with no usable papers keeps its band as the single search row
// rather than an empty heading: fieldFeedWorks resolves [] on any failure, so
// a dead feed degrades to a link and never to a hole.
function bandOf(
  field: Field,
  papers: FeedPaper[],
  ctx: ArmContext,
): HTMLElement {
  const band = group('field');
  if (papers.length === 0) {
    band.appendChild(rowList([searchRow(field, ctx)]));

    return band;
  }

  band.appendChild(microHeading(`Work by other researchers in ${field.name}`));
  band.appendChild(paperList(papers, ctx));
  band.appendChild(
    panelAction(`More in ${field.name}`, searchPath(field.name), () => {
      trackField(field, ctx);
    }),
  );

  return band;
}

// The reader standing here has just exhausted this author's list, and site
// search is one of the strongest time-on-site destinations the page can hand
// them — so after "more of this author" the footer continues the session with
// the author's own fields, filled with other researchers' work.
async function fieldBands(ctx: ArmContext): Promise<HTMLElement[]> {
  const fields = interests(FIELD_LIMIT);
  const feeds = await Promise.all(
    fields.map((field) => fieldFeedWorks(field.id)),
  );

  return fields.map((field, index) =>
    bandOf(
      field,
      // The list above IS this owner's work, so their own papers would make
      // the attribution heading a false claim.
      feeds[index]
        .filter((paper) => !paper.authorIds.includes(ctx.viewedUserId))
        .slice(0, BAND_SIZE),
      ctx,
    ),
  );
}

// The warm box: the beige fill carries the edge, so the frame takes a radius
// and no rule, and margin-top buys separation from the last card. The row
// hairlines and the group separators step up to WARM_HAIRLINE — rowsCss's
// default was tuned against white and disappears on the fill. flex-wrap lets
// the full-width authors line stack above a paper title; a single-anchor row
// never wraps.
function armCss(scope: string): string {
  return (
    panelCss(scope, { frame: 'warm' }) +
    kitPartCss('panel', scope, 'margin-top: 24px;') +
    rowsCss(scope) +
    kitPartCss(
      'row',
      scope,
      `border-top-color: ${WARM_HAIRLINE}; flex-wrap: wrap;`,
    ) +
    microHeadingCss(scope) +
    groupsCss(scope, { rule: `1px solid ${WARM_HAIRLINE}` }) +
    panelActionCss(scope) +
    `${scope} .${AUTHORS_CLASS} {` +
    ` flex: 1 0 100%; font-size: 13px; font-weight: 600; color: ${INK}; }`
  );
}

export const endOfProfile: ArmSpec = {
  stamp: (ctx) => ({
    render_state: footerFacts(ctx) != null ? 'rendered' : 'fallback',
  }),

  onReady: async (ctx) => {
    const facts = footerFacts(ctx);
    if (facts == null) return;

    // A sibling AFTER the pane, while infinite-scroll batches append INSIDE
    // it, so the footer sits below every appended card with no re-push logic
    // of its own — the slot's position is the re-push, and surface's own
    // tests pin that ordering.
    const slot = ctx.mountSlot('listEnd');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({
      title: `More about ${facts.name}`,
      subtitle: endLine(facts.firstName) ?? undefined,
    });
    const destinations = group('destinations');
    const list = rowList(linkRows(facts.pageName, ctx));
    destinations.appendChild(list);
    body.appendChild(destinations);
    slot.appendChild(root);

    appendMentionsWhenVerified(list, facts.pageName, ctx);

    // The stamp and markRendered describe the destinations footer, which
    // mounts before the feeds are asked; the bands extend it when they
    // settle, and below the fold a late append is invisible.
    ctx.markRendered();

    (await fieldBands(ctx)).forEach((band) => body.appendChild(band));
  },
};
